<script setup lang="ts">
  import { computed } from 'vue';
  import {
    DefaultTable,
    NoDocuments,
    SearchNoResults,
    TableHeaderSection,
    TableLoading,
  } from '@/shared/ui';
  import { RECORD_LIST_ORDER_OPTIONS } from '../lib/record-list-query';
  import RecordListTableRow from './RecordListTableRow.vue';
  import type { RecordListRow } from '../model/records.types';
  import type { RecordModalTarget } from '@/widgets/record-modals';
  import type { RecordRevisionsState } from '../model/useRecordExpansion';
  import type { RecordDeleteSelection } from '../lib/record-delete-confirmation';

  const props = withDefaults(
    defineProps<{
      records: RecordListRow[];
      loading: boolean;
      selectedIds?: Set<number>;
      selectedRevisionIds?: Set<number>;
      expandedIds?: Set<number>;
      allSelected?: boolean;
      partiallySelected?: boolean;
      hasActiveSearch?: boolean;
      isFilterActive?: boolean;
      currentSort?: string;
      currentOrder?: string;
      openMenuKey?: string | null;
      /** Only a SUPERUSER sees the "Inativar" switch. Decided by the page. */
      showInactivateToggle?: boolean;
      /**
       * Previous revisions of an expanded row, loaded on demand. The table never
       * fetches: it renders whatever state the owner reports for the row.
       */
      revisionsState?: (id: number) => RecordRevisionsState;
      /**
       * Whether a status write of the row is in flight. Reported by the owner, the same way the
       * revisions are, because the table does not write either.
       */
      isToggling?: (id: number) => boolean;
    }>(),
    {
      openMenuKey: null,
      selectedIds: () => new Set<number>(),
      selectedRevisionIds: () => new Set<number>(),
      expandedIds: () => new Set<number>(),
      allSelected: false,
      partiallySelected: false,
      hasActiveSearch: false,
      isFilterActive: false,
      currentSort: '',
      currentOrder: '',
      showInactivateToggle: false,
      // Inlined because a `withDefaults` default is hoisted out of setup() and so
      // cannot reference anything declared in this block. A default of a Function typed prop is
      // the value itself and not a factory, so these are the functions, not functions returning
      // them.
      revisionsState: (): RecordRevisionsState => ({ status: 'idle', revisions: [] }),
      isToggling: (): boolean => false,
    }
  );

  const emit = defineEmits<{
    'toggle-all': [];
    'toggle-selection': [id: number];
    'toggle-revision-selection': [id: number];
    'toggle-expand': [id: number];
    'retry-revisions': [id: number];
    'update:sort': [sortCamp: string, orderFlow: string];
    'filter-clicked': [];
    'update:open-menu-key': [key: string | null];
    'open-history': [target: RecordModalTarget];
    'open-control': [target: RecordModalTarget];
    edit: [target: RecordModalTarget];
    configure: [target: RecordModalTarget];
    'edit-homologation': [target: RecordModalTarget];
    delete: [selection: RecordDeleteSelection];
    'bulk-delete': [];
    'toggle-active': [id: number, isActive: boolean];
  }>();

  const HEADERS = [
    'Registro',
    'Título',
    'Indexador',
    'Rev.',
    'Status',
    'Registros gerados',
    'Ações',
  ];

  const isEmpty = computed(() => props.records.length === 0);

  /**
   * Deleting acts on whatever is checked, record or previous revision, so both
   * sets feed the state of the header.
   */
  const hasSelection = computed(
    () => props.selectedIds.size > 0 || props.selectedRevisionIds.size > 0
  );

  const stateColspan = computed(() => HEADERS.length + 1);

  const customThClass = (header: string) => {
    const baseClass = 'text-xs font-semibold text-gray-07 py-4.5 align-middle text-left';
    // The design puts "Registro" right next to the checkbox: the selection column
    // already reserves the 40 of the pair, so this one does not repeat the inset.
    if (header === 'Registro') {
      return `${baseClass} whitespace-nowrap`;
    }
    if (header === 'Título') {
      return `${baseClass} pl-6 min-w-88 w-full`;
    }
    if (header === 'Registros gerados') {
      return `${baseClass} pl-6 max-w-19`;
    }
    if (header === 'Ações') {
      return `${baseClass} pl-6 pr-6 whitespace-nowrap`;
    }
    return `${baseClass} pl-6 whitespace-nowrap`;
  };
</script>

<template>
  <DefaultTable class="divide-y divide-gray-02 min-w-full">
    <TableHeaderSection
      title="Lista de Registros"
      :headers="HEADERS"
      :item-count="props.records.length"
      item-type="registros"
      :has-selected="hasSelection"
      :selected-count="props.selectedIds.size + props.selectedRevisionIds.size"
      :selected-documents="[...props.selectedIds]"
      :all-selected="props.allSelected"
      :partially-selected="props.partiallySelected"
      :order-options="RECORD_LIST_ORDER_OPTIONS"
      :current-sort="props.currentSort"
      :current-order="props.currentOrder"
      :is-filter-active="props.isFilterActive"
      :delete-disabled="!hasSelection"
      :show-item-count="false"
      :show-column-headers="!props.loading && !isEmpty"
      :th-class="customThClass"
      th-checkbox-class="pl-4 pr-2 align-middle leading-[0]"
      @toggle-all="emit('toggle-all')"
      @update:sort="(sortCamp, orderFlow) => emit('update:sort', sortCamp, orderFlow)"
      @filter-clicked="emit('filter-clicked')"
      @delete="emit('bulk-delete')"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="props.loading">
        <td :colspan="stateColspan">
          <TableLoading
            data-testid="record-list-loading"
            :row-size="5"
            :column-size="stateColspan"
          />
        </td>
      </tr>
      <tr v-else-if="isEmpty">
        <td
          :colspan="stateColspan"
          class="pb-15"
        >
          <SearchNoResults
            v-if="props.hasActiveSearch"
            data-testid="record-list-no-results"
          />
          <NoDocuments
            v-else
            data-testid="record-list-empty"
            title="Nenhum registro encontrado"
          />
        </td>
      </tr>
      <template v-else>
        <template
          v-for="record in props.records"
          :key="record.id"
        >
          <RecordListTableRow
            :record="record"
            :selected="props.selectedIds.has(record.id)"
            :expanded="props.expandedIds.has(record.id)"
            :open-menu-key="props.openMenuKey"
            :show-inactivate-toggle="props.showInactivateToggle"
            :toggle-pending="props.isToggling(record.id)"
            @toggle-selection="emit('toggle-selection', record.id)"
            @toggle-expand="emit('toggle-expand', record.id)"
            @update:open-menu-key="emit('update:open-menu-key', $event)"
            @open-history="emit('open-history', $event)"
            @open-control="emit('open-control', $event)"
            @edit="emit('edit', $event)"
            @configure="emit('configure', $event)"
            @edit-homologation="emit('edit-homologation', $event)"
            @delete="emit('delete', $event)"
            @toggle-active="emit('toggle-active', record.id, $event)"
          />
          <template v-if="props.expandedIds.has(record.id)">
            <!--
              The previous revisions arrive after the row opens, so the wait and the
              failure are rows of their own: a spinner over the whole table would
              hide the record the user just expanded.
            -->
            <tr
              v-if="props.revisionsState(record.id).status === 'loading'"
              :data-testid="`record-revisions-loading-${record.id}`"
            >
              <td
                :colspan="stateColspan"
                class="py-4 text-center text-sm text-gray-06"
              >
                Carregando revisões anteriores...
              </td>
            </tr>
            <tr
              v-else-if="props.revisionsState(record.id).status === 'error'"
              :data-testid="`record-revisions-error-${record.id}`"
            >
              <td
                :colspan="stateColspan"
                class="py-4 text-center text-sm text-red-600"
              >
                Não foi possível carregar as revisões anteriores.
                <button
                  type="button"
                  class="ml-2 underline"
                  @click="emit('retry-revisions', record.id)"
                >
                  Tentar novamente
                </button>
              </td>
            </tr>
            <RecordListTableRow
              v-for="revision in props.revisionsState(record.id).revisions"
              :key="`${record.id}-${revision.id}`"
              :record="record"
              :revision="revision"
              :selected="props.selectedRevisionIds.has(revision.id)"
              :open-menu-key="props.openMenuKey"
              @toggle-selection="emit('toggle-revision-selection', revision.id)"
              @update:open-menu-key="emit('update:open-menu-key', $event)"
              @open-history="emit('open-history', $event)"
              @open-control="emit('open-control', $event)"
              @edit="emit('edit', $event)"
              @configure="emit('configure', $event)"
              @edit-homologation="emit('edit-homologation', $event)"
              @delete="emit('delete', $event)"
            />
          </template>
        </template>
      </template>
    </tbody>
  </DefaultTable>
</template>
